import { c as _c } from "react/compiler-runtime";
import { type Address, getChainAddresses } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useTokenBalance } from '@repo/web3';
import { useCallback, useMemo, useState } from 'react';
import { isAddressEqual } from 'viem';
import { useBalance } from 'wagmi';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useSpendableBalance } from '@/hooks/useSpendableBalance';
import { useWrappingGasReserve } from '@/hooks/useWrappingGasReserve';
import { computeWrappableNativeBalance } from '@/hooks/wrappingGasReserve.helpers';
import { computeNativeWrapSplit } from './marketAction.helpers';
export interface UseMarketLendNativeWrapParams {
  /** Loan token address (checked against the chain's wNative). */
  loanToken: Address | undefined;
  /** User-entered total to lend (ERC20 + native), in loan-token assets. */
  totalLend: bigint;
  /** Direct-lend flow gate — controls max, balances and wrapping. */
  enabled: boolean;
}

/**
 * Native-ETH wrapping for the "lend" path, the loan-side sister of
 * {@link useMarketCollateralNativeWrap}. The selected tab funds the entire leg from either the
 * ERC20 wNative balance or wrappable native ETH, so MAX is calculated independently per tab.
 */
export function useMarketLendNativeWrap(t0) {
  const $ = _c(38);
  const {
    loanToken,
    totalLend,
    enabled
  } = t0;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const [isWrapping, setIsWrapping] = useState(false);
  let t1;
  bb0: {
    if (!enabled || !loanToken) {
      t1 = false;
      break bb0;
    }
    let t2;
    if ($[0] !== loanToken || $[1] !== targetChainId) {
      const {
        wNative
      } = getChainAddresses(targetChainId);
      t2 = !!wNative && isAddressEqual(loanToken, wNative);
      $[0] = loanToken;
      $[1] = targetChainId;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const isWNativeLoan = t1;
  let t2;
  if ($[3] !== account.address || $[4] !== enabled || $[5] !== loanToken || $[6] !== targetChainId) {
    t2 = {
      chainId: targetChainId,
      userAddress: account.address,
      tokenAddress: loanToken,
      enabled,
      refetchInterval: 10000
    };
    $[3] = account.address;
    $[4] = enabled;
    $[5] = loanToken;
    $[6] = targetChainId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: erc20Balance,
    isLoading: isLoadingErc20Balance
  } = useTokenBalance(t2);
  const t3 = enabled && account.isConnected;
  let t4;
  if ($[8] !== erc20Balance || $[9] !== loanToken || $[10] !== t3 || $[11] !== targetChainId) {
    t4 = {
      chainId: targetChainId,
      token: loanToken,
      balance: erc20Balance,
      enabled: t3
    };
    $[8] = erc20Balance;
    $[9] = loanToken;
    $[10] = t3;
    $[11] = targetChainId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    spendableBalance: spendableErc20Balance,
    isLoading: isLoadingSpendableBalance
  } = useSpendableBalance(t4);
  const t5 = isWNativeLoan && account.isConnected;
  let t6;
  if ($[13] !== t5) {
    t6 = {
      enabled: t5,
      refetchInterval: 10000
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== account.address || $[16] !== t6 || $[17] !== targetChainId) {
    t7 = {
      address: account.address,
      chainId: targetChainId,
      query: t6
    };
    $[15] = account.address;
    $[16] = t6;
    $[17] = targetChainId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: nativeBalanceData,
    isLoading: isLoadingNativeBalance
  } = useBalance(t7);
  const nativeBalance = nativeBalanceData?.value ?? ZERO;
  const t8 = isWNativeLoan && account.isConnected;
  let t9;
  if ($[19] !== t8 || $[20] !== targetChainId) {
    t9 = {
      chainId: targetChainId,
      enabled: t8
    };
    $[19] = t8;
    $[20] = targetChainId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const {
    reserve: nativeGasReserve,
    isLoading: isLoadingGasReserve,
    isError: isGasReserveError
  } = useWrappingGasReserve(t9);
  let t10;
  if ($[22] !== nativeBalance || $[23] !== nativeGasReserve) {
    t10 = computeWrappableNativeBalance({
      nativeBalance,
      nativeGasReserve
    });
    $[22] = nativeBalance;
    $[23] = nativeGasReserve;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const wrappableNativeBalance = t10;
  const t11 = isWrapping && isWNativeLoan;
  let t12;
  if ($[25] !== t11 || $[26] !== totalLend) {
    t12 = computeNativeWrapSplit({
      totalAmount: totalLend,
      isWrapping: t11
    });
    $[25] = t11;
    $[26] = totalLend;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const {
    nativeAmount
  } = t12;
  const isWrapMaxReady = !(isWrapping && isWNativeLoan) || !isLoadingNativeBalance && !isLoadingGasReserve;
  let t13;
  bb1: {
    if (!enabled) {
      t13 = undefined;
      break bb1;
    }
    if (isWrapping && isWNativeLoan) {
      if (!isWrapMaxReady) {
        t13 = undefined;
        break bb1;
      }
      t13 = wrappableNativeBalance;
      break bb1;
    }
    t13 = spendableErc20Balance;
  }
  const maxLend = t13;
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = value => setIsWrapping(value);
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const onWrappingChange = t14;
  const t15 = enabled && account.isConnected && (isLoadingErc20Balance || isLoadingSpendableBalance || !isWrapMaxReady);
  const t16 = isLoadingNativeBalance || isLoadingGasReserve;
  const t17 = isWrapping && isGasReserveError && totalLend > (erc20Balance ?? ZERO) ? "Pay with the wrapped token to continue" : undefined;
  let t18;
  if ($[29] !== isWNativeLoan || $[30] !== isWrapping || $[31] !== maxLend || $[32] !== nativeAmount || $[33] !== t15 || $[34] !== t16 || $[35] !== t17 || $[36] !== wrappableNativeBalance) {
    t18 = {
      isWNativeLoan,
      isWrapping,
      onWrappingChange,
      wrappableNativeBalance,
      nativeAmount,
      maxLend,
      isMaxLoading: t15,
      isWrappingToggleLoading: t16,
      wrapErrorMessage: t17
    };
    $[29] = isWNativeLoan;
    $[30] = isWrapping;
    $[31] = maxLend;
    $[32] = nativeAmount;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
    $[36] = wrappableNativeBalance;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}